import { Injectable, isDevMode } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppConfig } from '../app.config';
import { context } from 'bookingbug-core-js';

@Injectable({
  providedIn: "root",
})
export class HeyCentricService {

  private url = "https://staging.bookinglab.co.uk";
  // private url = "http://localhost:4201";
  private path = "/";
  heyCentricUrlPath =
    window.location.hostname.indexOf("staging") !== -1
      ? "/payment/"
      : "/payment/";

  constructor(private http: HttpClient) { }

  // Normalise a pre-basket failure into one shape the summary component can
  // branch on. The backend's pre-payment gate answers 409 with
  // [{err: "Payment blocked before charge: <reason>"}] — `blocked: true` means
  // the basket itself is unusable (empty / not on this session), so retrying
  // the same basket is futile and the caller should rebuild it instead.
  private toPaymentError(err: any, body: any) {
    if (err.requestBody) {
      return err;
    }
    const blocked = err.status === 409;
    const reason = (blocked && Array.isArray(err.error) && err.error[0] && err.error[0].err)
      ? err.error[0].err
      : undefined;
    return { status: err.status, message: reason || err.message, blocked, reason, requestBody: body };
  }

  async preBasketInvoke(company, basket, serviceItems, descriptions, client, revCode, amounts, authToken, data, detailGroupId, slot, extraDuration, journeyRoute, parentCompanyId?) {
    let port = '';
    // '' is the default port for the scheme -- without it a plain https
    // page scored a stray ":" into the payment gateway URL.
    const ports: any = ['80', '443', ''];
    console.log('port', window.location.port);
    if (ports.indexOf(window.location.port) === -1) {
      port = `:${window.location.port}`;
    }

    let serviceItemString = '';
    for (const serviceItem of serviceItems) {
      // Backstop: never build a return URL with a missing service-item id
      // (e.g. ".../payment-verify/<company>/<basket>/,undefined"). If we reach
      // here without an id, an upstream basket/service-item call failed silently.
      if (!serviceItem || serviceItem.id == null) {
        throw new Error(
          `HeyCentric preBasketInvoke aborted: service item missing id ` +
          `(journeyRoute=${journeyRoute}, company=${company?.id}, basket=${basket?.id}, ` +
          `serviceItems=${JSON.stringify(serviceItems)})`
        );
      }
      serviceItemString = `${serviceItemString},${serviceItem.id}`;
    }

    console.log(`Journey route: ${journeyRoute}`);

    let returnUrl = '';
    if (journeyRoute == "order_a_copy_certificate") {
      returnUrl = `${window.location.protocol}//${window.location.hostname}${port}/start/${journeyRoute}/verify/${company.id}/${basket.id}/${serviceItemString}`;
    } else {
      returnUrl = `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify/${company.id}/${basket.id}/${serviceItemString}`;
    }

    console.log(`journeyRoute: ${journeyRoute} Return URL: ${returnUrl}`);

    // let returnUrl = `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify/${company.id}/${basket.id}/${serviceItemString}`;
    // if (journeyRoute !== 'ceremonies' && journeyRoute !== 'appointments') {
    //   returnUrl  = `${window.location.protocol}//${window.location.hostname}${port}/start/${journeyRoute}/verify/${company.id}/${basket.id}/${serviceItemString}`;
    // }

    const body = {
      host: `${context.apiUrl}`,
      // host: 'https://westminster-staging.council.jrni.com',
      // company_id MUST be the PARENT company (e.g. 37003). child_company stays the location.
      company_id: parentCompanyId === undefined ? `${company.id}` : parentCompanyId,
      child_company: `${company.id}`,
      client_id: `${client.id}`,
      basket: `${basket.id}`,
      service_items: serviceItems,
      descriptions: descriptions,
      // amount: amounts,
      data: data,
      return_url: `${returnUrl}`,
      auth_token: `${authToken}`,
      detail_group: detailGroupId,
      slot: slot,
      extra_duration: extraDuration,
      journey_route: journeyRoute
    }
    console.log(typeof revCode, Array.isArray(revCode));
    if (Array.isArray(amounts)) {
      body['amounts'] = amounts;
    }
    else {
      body['amount'] = `${amounts}`;
    }
    if (Array.isArray(revCode)) {
      body['rev_codes'] = revCode;
    }
    else {
      body['rev_code'] = `${revCode}`;
    }

    try {
      const response: any = await this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket`, body).toPromise();
      if (!response?.url) {
        throw { status: 0, message: 'No redirect URL in response', requestBody: body };
      }
      return response;
    } catch (err: any) {
      throw this.toPaymentError(err, body);
    }
  }

  async preBasketFind(companyId, basketId, serviceItemId) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
    };
    return this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket/find`, body).toPromise();
  }

  async preBasketUpdate(companyId, basketId, serviceItemId, status, ref, data) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
      ref: ref,
      status: `${status}`,
      data: data
    };
    return this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket/update`, body).toPromise();
  }

  async preBasketTelephonyInvoke(company, basket, serviceItems, descriptions, client, revCode, amounts, authToken, data, detailGroupId, slot, extraDuration, bu, agentId, agentEmail, agentPhone, journeyRoute, parentCompanyId?) {
    let serviceItemString = '';
    for (const serviceItem of serviceItems) {
      // Backstop: never build a return URL with a missing service-item id.
      if (!serviceItem || serviceItem.id == null) {
        throw new Error(
          `HeyCentric preBasketTelephonyInvoke aborted: service item missing id ` +
          `(journeyRoute=${journeyRoute}, company=${company?.id}, basket=${basket?.id}, ` +
          `serviceItems=${JSON.stringify(serviceItems)})`
        );
      }
      serviceItemString = `${serviceItemString},${serviceItem.id}`;
    }

    console.log(`Journey route: ${journeyRoute}`);

    let returnUrl = '';
    if (journeyRoute == "order_a_copy_certificate") {
      returnUrl = `${window.location.protocol}//${window.location.hostname}/start/${journeyRoute}/verify/${company.id}/${basket.id}/${serviceItemString}`;
    } else {
      returnUrl = `${window.location.protocol}//${window.location.hostname}${this.path}payment-verify/${company.id}/${basket.id}/${serviceItemString}`;
    }

    console.log(`journeyRoute: ${journeyRoute} Return URL: ${returnUrl}`);

    const body = {
      host: `${context.apiUrl}`,
      // company_id MUST be the PARENT company (e.g. 37003). child_company stays the location.
      company_id: parentCompanyId === undefined ? `${company.id}` : parentCompanyId,
      child_company: `${company.id}`,
      client_id: `${client.id}`,
      basket: `${basket.id}`,
      service_items: serviceItems,
      descriptions: descriptions,
      data: data,
      return_url: `${returnUrl}`,
      auth_token: `${authToken}`,
      detail_group: detailGroupId,
      slot: slot,
      extra_duration: extraDuration,
      bu: `${bu}`,
      "agent_id": `${agentId}`,
      "agent_email": `${agentEmail}`,
      "agent_phone": `${agentPhone}`,
      "journey_route": `${journeyRoute}`
    }
    if (Array.isArray(amounts)) {
      body['amounts'] = amounts;
    }
    else {
      body['amount'] = `${amounts}`;
    }
    if (Array.isArray(revCode)) {
      body['rev_codes'] = revCode;
    }
    else {
      body['rev_code'] = `${revCode}`;
    }

    try {
      const response: any = await this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket/arp`, body).toPromise();
      if (!response?.url) {
        throw { status: 0, message: 'No redirect URL in response', requestBody: body };
      }
      return response;
    } catch (err: any) {
      throw this.toPaymentError(err, body);
    }
  }
}